import { queryClient } from "@/lib/api/queryClient";
import { qk } from "@/lib/query/queryKeys";

/**
 * Cache bridge for the Velo AI-credit balance.
 *
 * The balance query (see {@link useAiCreditsBalance}) is deliberately NOT the
 * shared company cache: it owns the key below so a narrow AI-only projection
 * cannot poison consumers expecting the default company fields. The cost of
 * that isolation is that `qk.company.current` (= ["company", companyId])
 * does NOT prefix-match it — so every writer that changes usage or cap has to
 * come through here, or the ring silently keeps showing a stale number.
 *
 * The client is imported as the app-wide SINGLETON rather than taken from
 * `useQueryClient()` on purpose (same move as AiDataSyncBridge): the AI turn
 * lifecycle lives in callbacks inside AiAssistantProvider, and none of that
 * provider's test harnesses mount a QueryClientProvider. Invalidating a client
 * with no observers is a silent no-op, so this stays inert under test instead
 * of throwing.
 */
export const AI_CREDITS_KEY_PREFIX = ["company", "aiCredits"] as const;

/**
 * Pull the authoritative balance after a turn settled.
 *
 * Deliberately a refetch and NOT an optimistic decrement: what a turn costs
 * depends on how many model calls it made and on server config (the heavy
 * lane bills a multiple per call, and a per-message ceiling clamps the total),
 * so any number the browser predicts would be a claim about billing it cannot
 * back up. One small GET buys the truth instead.
 *
 * Also called MID-turn now, whenever a credits_charged frame reports a higher
 * total than the last one — an agentic turn can run for minutes, and the ring
 * must not sit on a balance from before it started.
 */
export function reconcileAiCredits(): void {
  void queryClient.invalidateQueries({ queryKey: AI_CREDITS_KEY_PREFIX });
  // In event scope the ring reads `aiCredits` off the event's entitlements —
  // the balance of the company that OWNS the event, which is the one a turn
  // debits. Same prefix the plan-purchase paths invalidate.
  void queryClient.invalidateQueries({ queryKey: qk.events.entitlementsPrefix });
}
